/**
 * Enhanced App Overview
 * Uses only real backend data (no mock stats)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { appsService, App, CreateAppResponse } from '../services/appsService'
import { useState } from 'react'
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  PlatformIcon,
  GridIcon,
  LoadingIcon,
  SettingsIcon,
  CopyIcon,
  WarningIcon,
  ErrorIcon
} from '../components/icons'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Card from '../components/ui/Card'
import { formatPlatform } from '../utils/platform'
import Badge from '../components/ui/Badge'
import { formatDate, formatTime } from '../utils/dateUtils'

interface AppOverviewProps {
  onSelectApp?: (appId: string) => void
  onNavigateToSettings?: (appId: string) => void
}

export default function AppOverview({ onSelectApp, onNavigateToSettings }: AppOverviewProps = {}) {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'last_active'>('name')
  const [platformFilter, setPlatformFilter] = useState<'all' | App['platform']>('all')
  const [portfolioFilter, setPortfolioFilter] = useState<'all' | 'included' | 'active'>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null)
  const [createdAppName, setCreatedAppName] = useState<string>('')
  const [newApp, setNewApp] = useState({
    name: '',
    bundle_id: '',
    platform: 'ios' as 'ios' | 'macos' | 'visionos' | 'tvos' | 'watchos' | 'android',
    current_version: '1.0.0',
  })

  const { data: apps, isLoading, error } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsService.getApps(),
  })

  const createAppMutation = useMutation({
    mutationFn: () => appsService.createApp(newApp),
    onSuccess: async (data: CreateAppResponse) => {
      setShowCreateModal(false)
      
      // Show the generated API key if it was returned
      if (data.api_key) {
        setCreatedApiKey(data.api_key)
        setCreatedAppName(data.name)
      }
      
      setNewApp({
        name: '',
        bundle_id: '',
        platform: 'ios',
        current_version: '1.0.0',
      })
      await queryClient.invalidateQueries({ queryKey: ['apps'] })
    },
  })

  const filteredApps = (apps || []).filter((app) => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.bundle_id.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesPlatform = platformFilter === 'all' || app.platform === platformFilter
    const matchesPortfolio = portfolioFilter === 'all' ||
      (portfolioFilter === 'included' && app.include_in_portfolio) ||
      (portfolioFilter === 'active' && app.is_active)

    return matchesSearch && matchesPlatform && matchesPortfolio
  })

  const sortedApps = [...filteredApps].sort((a, b) => {
    if (sortBy === 'last_active') {
      const aTime = a.last_event_received_at ? new Date(a.last_event_received_at).getTime() : 0
      const bTime = b.last_event_received_at ? new Date(b.last_event_received_at).getTime() : 0
      return bTime - aTime
    }
    return a.name.localeCompare(b.name)
  })

  const activeApps = (apps || []).filter((app) => app.is_active).length
  const portfolioApps = (apps || []).filter((app) => app.include_in_portfolio).length
  const platformOptions = Array.from(new Set((apps || []).map((app) => app.platform)))

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <LoadingIcon size="2xl" className="mx-auto mb-4 animate-spin text-primary-indigo" />
          <p className="text-gray-400">Loading apps...</p>
        </motion.div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <Card className="max-w-md">
          <div className="text-center">
            <ErrorStateGlyph />
            <h3 className="text-xl font-semibold mb-2 text-red-400">Failed to load apps</h3>
            <p className="text-gray-400 text-sm">{(error as Error).message}</p>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="dashboard-page">
      {/* Header */}
      <motion.div
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-6">
          <div className="max-w-3xl">
            <Badge variant="gradient" size="sm">Portfolio inventory</Badge>
            <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight text-balance">App Overview</h1>
            <p className="mt-2 text-gray-400 md:text-lg">
              {apps?.length || 0} app{apps?.length !== 1 ? 's' : ''}, {activeApps} active, {portfolioApps} included in portfolio analytics.
            </p>
          </div>

          <Button 
            variant="primary" 
            size="lg"
            icon={<PlusIcon size="md" />}
            className="w-full sm:w-auto"
            onClick={() => setShowCreateModal(true)}
          >
            Add App
          </Button>
        </div>

        {/* Search and filters */}
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<SearchIcon size="md" />}
            />
          </div>

          <div className="grid gap-2 sm:grid-cols-3 xl:flex xl:items-center">
            <SelectFilter
              label="Portfolio"
              value={portfolioFilter}
              onChange={(value) => setPortfolioFilter(value as typeof portfolioFilter)}
              options={[
                { value: 'all', label: 'All apps' },
                { value: 'included', label: 'In portfolio' },
                { value: 'active', label: 'Active apps' },
              ]}
            />
            <SelectFilter
              label="Platform"
              value={platformFilter}
              onChange={(value) => setPlatformFilter(value as typeof platformFilter)}
              options={[
                { value: 'all', label: 'All platforms' },
                ...platformOptions.map((platform) => ({ value: platform, label: formatPlatform(platform) })),
              ]}
            />
            <SelectFilter
              label="Sort"
              value={sortBy}
              onChange={(value) => setSortBy(value as typeof sortBy)}
              options={[
                { value: 'name', label: 'Name' },
                { value: 'last_active', label: 'Last active' },
              ]}
            />
          </div>
        </div>
      </motion.div>

      {/* App grid */}
      <div className="dashboard-content overflow-auto custom-scrollbar">
        {sortedApps.length === 0 ? (
          <motion.div
            className="h-full flex items-center justify-center px-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="max-w-md text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-3xl bg-indigo-500/15 text-indigo-200">
                <GridIcon size="2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{apps?.length ? 'No matching apps' : 'No apps yet'}</h3>
              <p className="text-gray-400 mb-6">
                {apps?.length ? 'Adjust the search or filters to reveal more of your portfolio.' : 'Add your first app to start tracking analytics.'}
              </p>
              {!apps?.length && (
                <Button 
                  variant="primary"
                  icon={<PlusIcon />}
                  className="w-full sm:w-auto"
                  onClick={() => setShowCreateModal(true)}
                >
                  Add Your First App
                </Button>
              )}
            </Card>
          </motion.div>
        ) : (
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {sortedApps.map((app, index) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
              >
                <AppCard app={app} onNavigateToSettings={onNavigateToSettings} onSelectApp={onSelectApp} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-xl">
            <h3 className="text-xl font-semibold mb-4">Add App</h3>
            {createAppMutation.isError && (
              <p className="text-red-400 text-sm mb-3">
                {(createAppMutation.error as Error).message || 'Failed to create app'}
              </p>
            )}
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault()
                createAppMutation.mutate()
              }}
            >
              <Input
                type="text"
                placeholder="App name"
                value={newApp.name}
                onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                required
              />
              <Input
                type="text"
                placeholder={newApp.platform === 'android'
                  ? 'Application ID (e.g. com.onethum.dokudoo)'
                  : 'Bundle ID (e.g. com.onethum.dokudoo)'}
                value={newApp.bundle_id}
                onChange={(e) => setNewApp({ ...newApp, bundle_id: e.target.value })}
                required
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={newApp.platform}
                  onChange={(e) => setNewApp({ ...newApp, platform: e.target.value as 'ios' | 'macos' | 'visionos' | 'tvos' | 'watchos' | 'android' })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white"
                >
                  <option className="bg-gray-900" value="ios">iOS</option>
                  <option className="bg-gray-900" value="macos">macOS</option>
                  <option className="bg-gray-900" value="visionos">visionOS</option>
                  <option className="bg-gray-900" value="tvos">tvOS</option>
                  <option className="bg-gray-900" value="watchos">watchOS</option>
                  <option className="bg-gray-900" value="android">Android</option>
                </select>
                <Input
                  type="text"
                  placeholder="Version (e.g. 1.0.0)"
                  value={newApp.current_version}
                  onChange={(e) => setNewApp({ ...newApp, current_version: e.target.value })}
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={createAppMutation.isPending}>
                  Create App
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* API Key Display Modal */}
      {createdApiKey && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <div className="mb-4">
              <h3 className="text-xl font-semibold mb-2">App created successfully</h3>
              <p className="text-gray-400 text-sm">
                Your app <strong className="text-white">{createdAppName}</strong> has been created with a default API key.
              </p>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <WarningIcon size="lg" className="mt-0.5 shrink-0 text-amber-300" />
                <div>
                  <p className="text-amber-300 font-semibold mb-1">Save this API key now!</p>
                  <p className="text-amber-200 text-sm">
                    This is the only time you'll be able to see the full key. 
                    Copy it now and store it securely. You can create additional keys later in Settings.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-4 border border-white/10 mb-4">
              <label className="text-xs text-gray-400 block mb-2">Your API Key</label>
              <code className="block text-sm break-all text-green-400 font-mono select-all">
                {createdApiKey}
              </code>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(createdApiKey)
                }}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <CopyIcon size="sm" />
                Copy to Clipboard
              </button>
              <button
                onClick={() => {
                  setCreatedApiKey(null)
                  setCreatedAppName('')
                }}
                className="btn-secondary flex-1"
              >
                I've Saved It
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              You can create additional API keys for this app anytime from Settings
            </p>
          </Card>
        </div>
      )}
    </div>
  )
}

interface AppCardProps {
  app: App
  onNavigateToSettings?: (appId: string) => void
  onSelectApp?: (appId: string) => void
}

function AppCard({ app, onNavigateToSettings, onSelectApp }: AppCardProps) {
  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onNavigateToSettings) {
      onNavigateToSettings(app.id)
    }
  }

  const handleCardClick = () => {
    if (onSelectApp) {
      onSelectApp(app.id)
    }
  }

  return (
    <Card 
      variant="interactive"
      hover
      className="relative group overflow-hidden cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Settings button - appears on hover */}
      <button
        className="absolute top-3 right-3 mobile-tap-target inline-flex items-center justify-center rounded-2xl bg-white/[0.08] backdrop-blur-sm border border-white/10 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 hover:bg-white/10 hover:border-white/20 z-10"
        onClick={handleSettingsClick}
        title="Edit app settings"
        aria-label={`Edit settings for ${app.name}`}
      >
        <SettingsIcon size="sm" className="text-gray-300" />
      </button>

      {/* App header */}
      <div className="flex items-start gap-4 mb-4">
        {/* App Icon with iOS-style rounded square */}
        <div className="relative flex-shrink-0">
          <div className="w-16 h-16 bg-gradient-to-br from-[#667EEA] to-[#764BA2] rounded-2xl flex items-center justify-center shadow-lg">
            {app.icon_url ? (
              <img src={app.icon_url} alt={app.name} className="w-full h-full rounded-2xl" />
            ) : (
              <PlatformIcon platform={app.platform} size="xl" className="text-white" />
            )}
          </div>
          {/* Platform badge */}
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gray-900 rounded-full flex items-center justify-center border-2 border-gray-800">
            <PlatformIcon platform={app.platform} size="xs" />
          </div>
        </div>

        {/* App info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg truncate mb-1">{app.name}</h3>
          <p className="text-xs text-gray-400 font-mono mb-1">{app.bundle_id}</p>
          <Badge variant="default" size="sm">
            v{app.current_version || '1.0.0'}
          </Badge>
        </div>
      </div>

      {/* Real metadata only */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
          <span className="text-gray-400">Platform</span>
          <span className="font-medium">{formatPlatform(app.platform)}</span>
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
          <span className="text-gray-400 flex-shrink-0">Bundle ID</span>
          <span className="font-mono text-xs ml-2">{app.bundle_id}</span>
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
          <span className="text-gray-400">Created</span>
          <span>{formatDate(app.created_at)}</span>
        </div>
      </div>

      {/* Last event timestamp */}
      {app.last_event_received_at && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-xs text-gray-500">
            Last event: {formatTime(app.last_event_received_at)}
          </p>
        </div>
      )}

    </Card>
  )
}

function SelectFilter({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
}) {
  return (
    <label className="relative block">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full min-h-12 rounded-2xl border border-white/10 bg-white/[0.055] px-4 py-3 pr-10 text-sm font-medium text-white appearance-none cursor-pointer backdrop-blur-md transition-all duration-300 hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/30 xl:w-44"
        aria-label={label}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-gray-900">
            {option.label}
          </option>
        ))}
      </select>
      <FilterIcon size="sm" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </label>
  )
}

function ErrorStateGlyph() {
  return (
    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-red-500/15 text-red-300">
      <ErrorIcon size="2xl" />
    </div>
  )
}
